import { useState, type FormEvent } from 'react';
import { setToken } from '../../lib/auth';
import { ApiError } from '../../lib/api';

export function LoginScreen() {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!value.startsWith('tt_') || !value.includes('.')) {
      setError('Token must be in the form tt_<id>.<secret>');
      return;
    }
    setSubmitting(true);
    try {
      setToken(value);
      const res = await fetch('/api/tokens', {
        headers: { Authorization: `Bearer ${value}` },
      });
      if (res.status === 401) {
        setToken(null);
        throw new ApiError(401, 'unauthorized', 'Token rejected by backend');
      }
      if (!res.ok) {
        setToken(null);
        throw new ApiError(res.status, 'unknown', `Backend returned ${res.status}`);
      }
      // If we got here, the token is valid. App.tsx re-renders on auth change.
    } catch (err) {
      setToken(null);
      setError(err instanceof Error ? err.message : 'Could not validate token');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-white">
      {/* Decorative pastel cloud — the brand's only "warm" moment. */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-pastel-soft"
      />
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full -z-10"
        style={{
          background:
            'radial-gradient(circle at 30% 30%, rgba(239, 44, 193, 0.18), transparent 60%), radial-gradient(circle at 70% 70%, rgba(189, 187, 255, 0.32), transparent 65%)',
          filter: 'blur(60px)',
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-60 -left-40 w-[720px] h-[720px] rounded-full -z-10"
        style={{
          background:
            'radial-gradient(circle at 40% 40%, rgba(252, 76, 2, 0.08), transparent 60%), radial-gradient(circle at 60% 60%, rgba(189, 187, 255, 0.28), transparent 70%)',
          filter: 'blur(80px)',
        }}
      />

      <div className="w-full max-w-md px-6">
        <div className="mb-10 text-center">
          <div className="mono-label text-black/50 mb-6">cloudflare-synced</div>
          <h1 className="font-display text-display font-medium text-black">
            time
            <br />
            tracker
          </h1>
          <p className="mt-6 font-display text-body-lg text-black/70 tracking-body">
            Cross-device reporting for the time you spend with agents and alone.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card p-6">
          <label htmlFor="token" className="block mono-label text-black mb-3">
            Paste your token
          </label>
          <input
            id="token"
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="tt_..."
            autoFocus
            className="w-full bg-glass-dark border border-border-light rounded-sharp px-3 py-2.5 font-mono text-sm text-black placeholder-black/30 outline-none focus:border-midnight transition-colors"
          />

          {error && (
            <p className="mt-3 mono-label text-[#b00020] leading-[1.4] normal-case tracking-normal font-display text-caption">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || value.length === 0}
            className="btn-primary w-full mt-5"
          >
            {submitting ? 'Checking…' : 'Enter'}
          </button>

          <p className="mt-4 font-display text-caption text-black/50 tracking-body">
            Don't have one? Run{' '}
            <code className="font-mono text-[12px] bg-glass-dark px-1.5 py-0.5 rounded-sharp">
              POST /admin/bootstrap
            </code>{' '}
            against your backend.
          </p>
        </form>
      </div>
    </div>
  );
}
